import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../../../context/AuthContext';
import {
  getUserPreferences,
  updateUserPreferences,
  exportUserData,
  requestAccountDeletion,
} from '../../../../firebase/services';
import { generateExportPDF, generateExportCSV } from '../../../../utils/exportUtils';
import {
  getShareNameMode,
  resolveShareName,
  SHARE_NAME_MODES,
  SHARE_CUSTOM_NAME_MAX,
} from '../../../../utils/shareUtils';
import ToggleSwitch from '../../../common/ToggleSwitch';
import DeleteAccountModal from '../../../common/DeleteAccountModal';
import './settings.css';

const PrivacySettingsScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, logout } = useContext(AuthContext);
  const [nameMode, setNameMode] = useState('account');
  const [customName, setCustomName] = useState('');
  const [savedCustomName, setSavedCustomName] = useState('');
  const [isAdult, setIsAdult] = useState(false);
  const [downloadState, setDownloadState] = useState('idle'); // 'idle' | 'loading' | 'success'
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) return;
    getUserPreferences(currentUser.uid)
      .then(prefs => {
        setNameMode(getShareNameMode(prefs));
        setCustomName(prefs.shareCustomName || '');
        setSavedCustomName(prefs.shareCustomName || '');
        setIsAdult(prefs.isAdultConfirmed === true);
      })
      .catch(err => console.error('Failed to load privacy preferences:', err))
      .finally(() => setLoading(false));
  }, [currentUser]);

  // Name on share cards (adults only). shareAnonymous stays in sync for older clients.
  const handleNameMode = async (mode) => {
    const previous = nameMode;
    setNameMode(mode);
    try {
      await updateUserPreferences(currentUser.uid, { shareNameMode: mode, shareAnonymous: mode === 'hidden' });
    } catch (err) {
      console.error('Failed to save share name preference:', err);
      setNameMode(previous);
    }
  };

  const saveCustomName = async () => {
    const value = customName.trim().slice(0, SHARE_CUSTOM_NAME_MAX);
    setCustomName(value);
    if (value === savedCustomName) return;
    try {
      await updateUserPreferences(currentUser.uid, { shareCustomName: value });
      setSavedCustomName(value);
    } catch (err) {
      console.error('Failed to save custom share name:', err);
    }
  };

  const previewName = resolveShareName(
    { isAdultConfirmed: isAdult, shareNameMode: nameMode, shareCustomName: customName },
    currentUser?.displayName
  );

  // Release 14: names only appear on public shares for confirmed adults
  const handleAdultToggle = async (newIsOn) => {
    setIsAdult(newIsOn);
    try {
      await updateUserPreferences(currentUser.uid, { isAdultConfirmed: newIsOn });
    } catch (err) {
      console.error('Failed to save age confirmation:', err);
      setIsAdult(!newIsOn);
    }
  };

  const handleDownload = async () => {
    setDownloadState('loading');
    try {
      const data = await exportUserData(currentUser.uid);
      const today = new Date().toISOString().slice(0, 10);

      // Download JSON
      const json = JSON.stringify(data, null, 2);
      const jsonBlob = new Blob([json], { type: 'application/json' });
      const jsonUrl = URL.createObjectURL(jsonBlob);
      const jsonLink = document.createElement('a');
      jsonLink.href = jsonUrl;
      jsonLink.download = `lightway-data-${today}.json`;
      document.body.appendChild(jsonLink);
      jsonLink.click();
      document.body.removeChild(jsonLink);
      URL.revokeObjectURL(jsonUrl);

      // Download PDF
      const pdfDoc = generateExportPDF(data);
      pdfDoc.save(`lightway-advocacy-report-${today}.pdf`);

      // Download CSV
      const csvContent = generateExportCSV(data);
      const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const csvUrl = URL.createObjectURL(csvBlob);
      const csvLink = document.createElement('a');
      csvLink.href = csvUrl;
      csvLink.download = `lightway-activities-${today}.csv`;
      document.body.appendChild(csvLink);
      csvLink.click();
      document.body.removeChild(csvLink);
      URL.revokeObjectURL(csvUrl);

      setDownloadState('success');
      setTimeout(() => setDownloadState('idle'), 3000);
    } catch (err) {
      console.error('Export failed:', err);
      setDownloadState('idle');
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await requestAccountDeletion(currentUser.uid);
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Failed to request account deletion:', err);
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="settings-screen">
      <header className="settings-header">
        <button onClick={() => navigate('/profile')} className="back-button">
          &larr;
        </button>
        <h1>{t('privacy.title')}</h1>
      </header>
      <main className="settings-content" style={{ textAlign: 'left' }}>
        {!loading && (
          <>
            {/* Section 1: Age confirmation + Share Display Name */}
            <div className="settings-pref-card privacy-section">
              <div className="settings-pref-row">
                <div className="settings-pref-text">
                  <span className="settings-pref-label">{t('privacy.isAdult')}</span>
                  <span className="settings-pref-subtext">
                    {t('privacy.isAdultDesc')}
                  </span>
                </div>
                <ToggleSwitch
                  isOn={isAdult}
                  onToggle={handleAdultToggle}
                />
              </div>
              <fieldset className="share-name-options" disabled={!isAdult}>
                <legend className="settings-pref-label">{t('privacy.shareNameLabel')}</legend>
                <span className="settings-pref-subtext">
                  {isAdult ? t('privacy.shareNameDesc') : t('privacy.nameHiddenUnder18')}
                </span>
                {SHARE_NAME_MODES.map(mode => (
                  <label key={mode} className="share-name-option">
                    <input
                      type="radio"
                      name="shareNameMode"
                      value={mode}
                      checked={isAdult ? nameMode === mode : mode === 'hidden'}
                      onChange={() => handleNameMode(mode)}
                    />
                    <span>{t(`privacy.shareName_${mode}`)}</span>
                  </label>
                ))}
                {isAdult && nameMode === 'custom' && (
                  <input
                    type="text"
                    className="share-name-custom"
                    value={customName}
                    maxLength={SHARE_CUSTOM_NAME_MAX}
                    placeholder={t('privacy.shareNameCustomPlaceholder')}
                    aria-label={t('privacy.shareName_custom')}
                    onChange={e => setCustomName(e.target.value)}
                    onBlur={saveCustomName}
                    onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  />
                )}
                <span className="settings-pref-subtext share-name-preview">
                  {t('privacy.shareNamePreview', { name: previewName })}
                </span>
              </fieldset>
            </div>

            {/* Section 2: Download My Data */}
            <div className="settings-pref-card privacy-section">
              <p className="settings-pref-label" style={{ margin: '0 0 4px' }}>{t('privacy.downloadData')}</p>
              <p className="settings-pref-subtext" style={{ margin: '0 0 8px' }}>
                {t('privacy.downloadDataDesc')}
              </p>
              <p className="settings-pref-subtext" style={{ margin: '0 0 4px' }}>
                <strong>PDF</strong> — {t('privacy.downloadDataPDF')}
              </p>
              <p className="settings-pref-subtext" style={{ margin: '0 0 4px' }}>
                <strong>CSV</strong> — {t('privacy.downloadDataCSV')}
              </p>
              <p className="settings-pref-subtext" style={{ margin: '0 0 16px' }}>
                <strong>JSON</strong> — {t('privacy.downloadDataJSON')}
              </p>
              <button
                className="privacy-action-button privacy-action-button--navy"
                onClick={handleDownload}
                disabled={downloadState === 'loading'}
              >
                {downloadState === 'loading' && t('privacy.downloadPreparing')}
                {downloadState === 'success' && t('privacy.downloadComplete')}
                {downloadState === 'idle' && t('privacy.downloadData')}
              </button>
            </div>

            {/* Section 3: Delete My Account */}
            <div className="settings-pref-card privacy-section">
              <button
                className="privacy-action-button privacy-action-button--destructive"
                onClick={() => setShowDeleteModal(true)}
              >
                {t('privacy.deleteAccount')}
              </button>
            </div>
          </>
        )}
      </main>

      {showDeleteModal && (
        <DeleteAccountModal
          onCancel={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
};

export default PrivacySettingsScreen;
